import React, { useState } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Table,
  ActionIcon,
  Badge,
  Text,
  TextInput,
  LoadingOverlay,
  Avatar,
  Tooltip,
  Pagination,
  Select,
  Stack,
  Flex,
  Card,
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconEye,
  IconSearch,
  IconSchool,
  IconUsers,
  IconMapPin,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useSchools } from '../../hooks/useSchools';
import type { School } from '../../types/api';

const Schools: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState('25');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const {
    data: schoolsData,
    isLoading,
    error,
  } = useSchools({
    page,
    per_page: parseInt(perPage),
    search: debouncedSearch,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const schools = schoolsData?.schools || [];
  const pagination = schoolsData?.pagination;

  const handleCreateSchool = () => {
    navigate('/admin/schools/new');
  };

  const handleEditSchool = (school: School) => {
    navigate(`/admin/schools/${school.id}/edit`);
  };

  const handleViewSchool = (school: School) => {
    navigate(`/admin/schools/${school.id}`);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSchoolTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      elementary: 'blue',
      middle: 'green',
      high: 'orange',
      kindergarten: 'purple',
      mixed: 'cyan',
    };
    return colors[type] || 'gray';
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  if (error) {
    notifications.show({
      title: 'Error',
      message: 'Failed to load schools. Please try again.',
      color: 'red',
    });
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Schools</Title>
          <Text size="sm" c="dimmed">
            Manage schools and their settings
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={handleCreateSchool}
        >
          Add School
        </Button>
      </Group>

      {/* Filters and Search */}
      <Card>
        <Group justify="space-between" mb="md">
          <TextInput
            placeholder="Search schools..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flexGrow: 1, maxWidth: 400 }}
          />
          <Group>
            <Select
              label="Per page"
              value={perPage}
              onChange={(value) => {
                setPerPage(value || '25');
                setPage(1);
              }}
              data={[
                { value: '10', label: '10' },
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
              style={{ width: 100 }}
            />
          </Group>
        </Group>
      </Card>

      {/* Schools Table */}
      <Paper withBorder>
        <LoadingOverlay visible={isLoading} />
        
        {schools.length === 0 && !isLoading ? (
          <Flex direction="column" align="center" justify="center" py="xl">
            <IconSchool size={48} color="gray" />
            <Title order={4} c="dimmed" mt="md">
              No schools found
            </Title>
            <Text size="sm" c="dimmed" ta="center" mt="xs">
              {search
                ? 'Try adjusting your search criteria'
                : 'Get started by adding your first school'}
            </Text>
            {!search && (
              <Button
                variant="outline"
                leftSection={<IconPlus size={16} />}
                onClick={handleCreateSchool}
                mt="md"
              >
                Add School
              </Button>
            )}
          </Flex>
        ) : (
          <>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    School {getSortIcon('name')}
                  </Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Contact</Table.Th>
                  <Table.Th>Location</Table.Th>
                  <Table.Th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('student_count')}
                  >
                    Students {getSortIcon('student_count')}
                  </Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th style={{ width: 120 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {schools.map((school: any) => (
                  <Table.Tr key={school.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar size="sm" radius="sm">
                          <IconSchool size={16} />
                        </Avatar>
                        <div>
                          <Text fw={500} size="sm">
                            {school.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {school.principal_name}
                          </Text>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={getSchoolTypeColor(school.school_type)}
                        variant="light"
                        size="sm"
                      >
                        {school.school_type}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        {school.email && (
                          <Group gap={4}>
                            <IconMail size={12} />
                            <Text size="xs">{school.email}</Text>
                          </Group>
                        )}
                        {school.phone && (
                          <Group gap={4}>
                            <IconPhone size={12} />
                            <Text size="xs">{school.phone}</Text>
                          </Group>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {school.address && (
                        <Group gap={4}>
                          <IconMapPin size={12} />
                          <Text size="xs" lineClamp={2}>
                            {school.address}
                          </Text>
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <IconUsers size={12} />
                        <Text size="sm">
                          {school.student_count || 0}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={school.active ? 'green' : 'red'}
                        variant="light"
                        size="sm"
                      >
                        {school.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="View details">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleViewSchool(school)}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Edit school">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleEditSchool(school)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <Group justify="center" p="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={pagination.total_pages}
                  size="sm"
                />
              </Group>
            )}
          </>
        )}
      </Paper>

      {/* Summary Stats */}
      {pagination && (
        <Text size="sm" c="dimmed" ta="center">
          Showing {schools.length} of {pagination.total_count} schools
          {search && ` for "${search}"`}
        </Text>
      )}
    </Stack>
  );
};

export default Schools;